/**
 * DETAIL SIDEBAR REDUCER
 *
 * A generic sidebar for viewing/editing any entity type.
 * Works the same way as modalReducer: tracks open state, content type, data, and meta.
 *
 * Examples:
 *  - Loan borrowers, contacts, subject properties
 *  - Pipeline deals, users, tasks — anything that needs a detail panel
 */

const detailSidebarInit = {
  isOpen: false,
  contentType: null,
  data: null,
  entityId: null,
  meta: {
    title: null,
    mode: "view", // "view" | "edit" | "create"
    source: null,
    sourceField: null,
    preventClose: false,
    isDirty: false,
    key: null,
  },
};

// Content type constants — extend as needed
export const DETAIL_SIDEBAR_CONTENT_TYPES = {
  BORROWER: "BORROWER",
  LOAN_RELATED_CONTACT: "LOAN_RELATED_CONTACT",
  SUBJECT_PROPERTY: "SUBJECT_PROPERTY",
};

const detailSidebarReducer = (state = detailSidebarInit, action) => {
  switch (action.type) {
    case "OPEN_DETAIL_SIDEBAR":
      return {
        ...state,
        isOpen: true,
        contentType: action.payload.contentType,
        data: action.payload.data ?? null,
        entityId: action.payload.entityId ?? null,
        meta: {
          ...detailSidebarInit.meta,
          ...(action.payload.meta || {}),
          key:
            action.payload.meta?.key ||
            `${action.payload.contentType}_${action.payload.entityId || Date.now()}`,
        },
      };

    case "CLOSE_DETAIL_SIDEBAR":
      return { ...state, isOpen: false };

    case "CLOSE_AND_CLEAR_DETAIL_SIDEBAR":
      return detailSidebarInit;

    case "SET_DETAIL_SIDEBAR_DATA":
      return { ...state, data: action.payload };

    case "UPDATE_DETAIL_SIDEBAR_DATA":
      return {
        ...state,
        data: { ...state.data, ...action.payload },
        meta: { ...state.meta, isDirty: true },
      };

    case "SET_DETAIL_SIDEBAR_META":
      return { ...state, meta: action.payload };

    case "UPDATE_DETAIL_SIDEBAR_META":
      return {
        ...state,
        meta: { ...state.meta, ...action.payload },
      };

    case "SWITCH_DETAIL_SIDEBAR_ENTITY":
      return {
        ...state,
        contentType: action.payload.contentType ?? state.contentType,
        data: action.payload.data ?? null,
        entityId: action.payload.entityId ?? null,
        meta: {
          ...state.meta,
          ...(action.payload.meta || {}),
          isDirty: false,
          key:
            action.payload.meta?.key ||
            `${action.payload.contentType || state.contentType}_${action.payload.entityId || Date.now()}`,
        },
      };

    case "CLEAR_DETAIL_SIDEBAR":
      return detailSidebarInit;

    default:
      return state;
  }
};

// ─── ACTION CREATORS ─────────────────────────────────────────────

export const detailSidebarOpen = (
  contentType,
  data = null,
  entityId = null,
  meta = {},
) => ({
  type: "OPEN_DETAIL_SIDEBAR",
  payload: { contentType, data, entityId, meta },
});

export const detailSidebarClose = () => ({
  type: "CLOSE_DETAIL_SIDEBAR",
});

export const detailSidebarCloseAndClear = () => ({
  type: "CLOSE_AND_CLEAR_DETAIL_SIDEBAR",
});

export const detailSidebarSetData = (data) => ({
  type: "SET_DETAIL_SIDEBAR_DATA",
  payload: data,
});

export const detailSidebarUpdateData = (partialData) => ({
  type: "UPDATE_DETAIL_SIDEBAR_DATA",
  payload: partialData,
});

export const detailSidebarSetMeta = (meta) => ({
  type: "SET_DETAIL_SIDEBAR_META",
  payload: meta,
});

export const detailSidebarUpdateMeta = (partialMeta) => ({
  type: "UPDATE_DETAIL_SIDEBAR_META",
  payload: partialMeta,
});

export const detailSidebarSwitchEntity = (
  contentType,
  data = null,
  entityId = null,
  meta = {},
) => ({
  type: "SWITCH_DETAIL_SIDEBAR_ENTITY",
  payload: { contentType, data, entityId, meta },
});

export const detailSidebarClear = () => ({
  type: "CLEAR_DETAIL_SIDEBAR",
});

export default detailSidebarReducer;
